import React, { useState } from 'react'
import RestaurantCard from '../components/Restaurant/Card'
import { Link } from 'react-router-dom'
import { useQuery, gql } from '@apollo/client'
import { restaurantByOwner } from '../apollo'
import CreateRestaurant from '../components/Restaurant/CreateRestaurant'
import {
  Alert,
  Box,
  Button,
  Modal,
  Container,
  Grid,
  Snackbar,
  Typography
} from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import StorefrontRounded from '@mui/icons-material/StorefrontRounded'
import BadgeRounded from '@mui/icons-material/BadgeRounded'
import RestaurantMenuRounded from '@mui/icons-material/RestaurantMenuRounded'
import RocketLaunchRounded from '@mui/icons-material/RocketLaunchRounded'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import { IconTile, raisedButtonSx } from '../components/UI/Depth'
import { cardSx } from '../utils/depth'
import { withTranslation } from 'react-i18next'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const RESTAURANT_BY_OWNER = gql`
  ${restaurantByOwner}
`
const Restaurant = props => {
  const { t } = props
  const [owner, setOwner] = useState()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [createdMessage, setCreatedMessage] = useState('')
  const vendorId = localStorage.getItem('vendorId')
  const toggleModal = () => {
    setIsModalVisible(prevState => !prevState)
  }

  const { data, error: errorQuery, loading: loadingQuery } = useQuery(
    RESTAURANT_BY_OWNER,
    {
      variables: { id: vendorId }
    }
  )
  const links =
    data &&
    data.restaurantByOwner.restaurants.map((rest, index) => {
      return (
        <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
          <Link
            underline="none"
            style={{ textDecoration: 'none' }}
            key={rest._id}
            onClick={() => {
              localStorage.setItem('restaurantId', rest._id)
              localStorage.setItem('restaurantImage', rest.image)
              localStorage.setItem('restaurantName', rest.name)
            }}
            to={`/admin/dashboard/${rest.slug}`}>
            <RestaurantCard key={rest._id} rest={rest} />
          </Link>
        </Grid>
      )
    })
  const owner_ = data && data.restaurantByOwner
  const stores = (owner_ && owner_.restaurants) || []
  const openCreate = () => {
    setOwner(owner_ ? owner_._id : vendorId)
    toggleModal()
  }
  const addButton = (
    <Button
      startIcon={<AddRounded />}
      onClick={openCreate}
      disabled={loadingQuery || !!errorQuery}
      sx={raisedButtonSx}>
      {t('AddNewRestaurant')}
    </Button>
  )

  return (
    <>
      <Header
        title={t('VendorStores')}
        subtitle={
          owner_ && owner_.email
            ? `${owner_.email} · ${stores.length} ${
                stores.length === 1 ? t('StoreSingular') : t('Stores')
              }`
            : t('VendorStoresHelp')
        }
        icon={StorefrontRounded}
        action={stores.length ? addButton : null}
      />
      <Container disableGutters maxWidth={false}>
        {loadingQuery ? <CustomLoader /> : null}
        {errorQuery ? (
          <Typography sx={{ color: 'error.main', mb: 2 }}>
            {errorQuery.message}
          </Typography>
        ) : null}
        {!loadingQuery && !errorQuery && stores.length > 0 ? (
          <Grid container spacing={2.5}>
            {links}
          </Grid>
        ) : null}
        {!loadingQuery && !errorQuery && stores.length === 0 ? (
          <Box
            sx={{
              ...cardSx,
              position: 'relative',
              overflow: 'hidden',
              textAlign: 'center',
              px: { xs: 3, sm: 6 },
              py: { xs: 6, sm: 8 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(600px 220px at 50% 0%, rgba(244,102,31,0.10), transparent 70%)',
                pointerEvents: 'none'
              }
            }}>
            <Box sx={{ position: 'relative', mb: 3 }}>
              <IconTile icon={StorefrontRounded} size={88} />
              <Box
                sx={{
                  position: 'absolute',
                  right: -10,
                  bottom: -10,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'common.white',
                  color: 'primary.main',
                  boxShadow: '0 6px 14px -6px rgba(16,24,40,0.4)'
                }}>
                <AddRounded sx={{ fontSize: 22 }} />
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 22, sm: 26 },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: 'text.primary'
              }}>
              {t('NoStoresYet')}
            </Typography>
            <Typography
              sx={{
                mt: 1,
                maxWidth: 460,
                fontSize: 15,
                color: 'text.secondary'
              }}>
              {t('NoStoresYetHelp')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1.5,
                mt: 4,
                mb: 4,
                width: '100%',
                maxWidth: 640
              }}>
              {[
                [BadgeRounded, t('NoStoresStepDetails')],
                [RestaurantMenuRounded, t('NoStoresStepMenu')],
                [RocketLaunchRounded, t('NoStoresStepLive')]
              ].map(([Icon, label], index) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: '#FAFAFB',
                    border: '1px solid rgba(16,24,40,0.06)',
                    textAlign: 'left'
                  }}>
                  <Icon sx={{ color: 'primary.main', fontSize: 22 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {index + 1}. {label}
                  </Typography>
                </Box>
              ))}
            </Box>
            {addButton}
          </Box>
        ) : null}
        <Modal open={isModalVisible} onClose={toggleModal} sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            <CreateRestaurant
              owner={owner}
              onClose={toggleModal}
              onCreated={message => {
                setIsModalVisible(false)
                setCreatedMessage(message)
              }}
            />
          </Box>
        </Modal>
        <Snackbar
          open={!!createdMessage}
          autoHideDuration={4000}
          onClose={() => setCreatedMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setCreatedMessage('')}>
            {createdMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  )
}
export default withTranslation()(Restaurant)
